/**
 * A escolha de contexto na entrada — T3 e §7 da spec #408.
 *
 * Só chega aqui quem alcança mais de um contexto e não tem padrão defensável: com um vínculo
 * só, ou sendo super admin (que vai direto para a Plataforma), ninguém pergunta nada.
 *
 * As empresas que uma clínica atende NÃO viram opções: elas são a linha de detalhe do cartão
 * da clínica. Uma clínica que atende doze empresas é uma escolha, não doze — quem escolhe a
 * clínica já enxerga as doze depois.
 *
 * O vazio desta tela é o único caso do sistema em que não há próximo passo: quem não tem
 * vínculo nenhum não pode se vincular sozinho, então a tela diz a quem pedir e não oferece
 * botão — um que abrisse formulário levaria a 403.
 */

import React from 'react';
import { Box, Card, CardActionArea, Chip, Paper, Stack, Typography } from '@mui/material';
import {
  Business as BusinessIcon,
  ChevronRight as ChevronRightIcon,
  MedicalServices as MedicalServicesIcon,
  Public as PublicIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import { EmptyState, PageShell } from '../../components/common';
import { useManifesto } from '../../stores/manifestoStore';
import type { ContextoDisponivel, Perspectiva } from '../../types/manifesto';
import { NOME_DA_PERSPECTIVA, serializarContexto } from '../../utils/manifestoMenu';

const ICONE_DO_TIPO: Record<Perspectiva, typeof BusinessIcon> = {
  empresa: BusinessIcon,
  prestador: MedicalServicesIcon,
  plataforma: PublicIcon,
};

/** O tipo precisa ser lido de relance: é ele que muda o que a pessoa vê depois de entrar. */
const COR_DO_SELO: Record<Perspectiva, 'primary' | 'secondary' | 'default'> = {
  empresa: 'primary',
  prestador: 'secondary',
  plataforma: 'default',
};

const SEM_VINCULO = {
  title: 'Seu acesso ainda não foi vinculado',
  description:
    'Sua conta existe, mas ainda não está ligada a nenhuma empresa nem clínica — é esse vínculo que define o que você enxerga no sistema.',
};

export const ContextChoicePage: React.FC = () => {
  const { manifesto, escolhaPendente, trocarContexto } = useManifesto();
  const navigate = useNavigate();

  // Chegando pela recusa 403 não há manifesto: as opções vêm com a própria recusa.
  const contextos = manifesto?.contextos ?? escolhaPendente ?? [];

  const escolher = async (contexto: ContextoDisponivel) => {
    await trocarContexto(contexto);
    navigate('/', { replace: true });
  };

  if (contextos.length === 0) {
    return (
      <PageShell bare breadcrumbs={[]}>
        <EmptyState config={SEM_VINCULO} />

        <Paper
          variant='outlined'
          sx={{ maxWidth: 460, mx: 'auto', mt: 3, p: 2, bgcolor: 'action.hover' }}
        >
          <Typography variant='subtitle2' fontWeight={600} gutterBottom>
            Quem resolve isso
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            Peça a quem administra os usuários da sua empresa ou clínica para vincular o seu
            acesso.
          </Typography>
        </Paper>
      </PageShell>
    );
  }

  return (
    <PageShell
      bare
      breadcrumbs={[]}
      title='Onde você vai trabalhar agora?'
      subtitle='Você atende em mais de um lugar. Escolha por onde começar — dá para trocar depois pelo cabeçalho.'
    >
      <Stack spacing={2} sx={{ maxWidth: 560 }}>
        {contextos.map((contexto) => {
          const Icone = ICONE_DO_TIPO[contexto.tipo];

          return (
            <Card key={serializarContexto(contexto)} variant='outlined'>
              <CardActionArea onClick={() => void escolher(contexto)} sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Chip
                    size='small'
                    color={COR_DO_SELO[contexto.tipo]}
                    icon={<Icone fontSize='small' />}
                    label={NOME_DA_PERSPECTIVA[contexto.tipo]}
                  />

                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography variant='subtitle1' fontWeight={600}>
                      {contexto.nome}
                    </Typography>
                    {contexto.detalhe && (
                      <Typography variant='body2' color='text.secondary'>
                        {contexto.detalhe}
                      </Typography>
                    )}
                  </Box>

                  <ChevronRightIcon color='action' />
                </Box>
              </CardActionArea>
            </Card>
          );
        })}
      </Stack>
    </PageShell>
  );
};

export default ContextChoicePage;
